import Delete from "csi/action/Delete";
import { QRY_LIMIT_PATH } from "csi/constants";
import { Action } from "csi/def/Action";
import Actions from "csi/def/Actions";
import { Context } from "csi/def/Context";
import Request from "csi/def/Request";
import ViewRef from "csi/def/ViewRef";
import StoreNodesConstructor from "csi/store/Nodes";
import _GroupByRefMixinConstructor from "csi/table/_GroupByRefMixin";
import TableWidgetConstructor, { TableWidget } from "csi/table/Widget";
import { MultiSelectSwitchboard } from "csi/toolbar/MultiSelectSwitchboard";
import { getUrlPath, pathGetStepOperand, pathGetSteps } from "csi/utility/pathUtility";
import { QueryLevel } from "csi/view/_query/Level";
import MessageDialog from "csi/widget/MessageDialog";
import xml from "csi/xml";
import Query from "csi/xml/Query";
import declare from "dojo/_base/declare";
import lang from "dojo/_base/lang";
import { ATTR_PARENT, C8Node } from "src/c8-platform/type/node";
import AddToAffectedItemsGroupController from "./AddToAffectedItemsGroupController";
import AffectedItemsGroupController from "./AffectedItemsGroupController";
import AffectedItemsGroupDeleteController from "./AffectedItemsGroupDeleteController";
import FetchAdditionalImpactedItemsGroupController from "./FetchAdditionalImpactedItemsGroupController";

const EC_ITEM_TYPE_FORMULATED_PRODUCT_TYPES = ["ECItemType:Food", "ECItemType:FormulatedMaterial", "ECItemType:FoodIngredient"];
const ADD_TO_AFFECTED_ITEMS = "AddToAffectedItems";
const AFFECTED_ITEM_CALCULATE_IMPACTED_ITEM_PCT = "AffectedItemCalculateImpactedItemPct";
const FETCH_ADDITIONAL_IMPACTED_ITEMS = "FetchAdditionalImpactedItems";
const RESET_IMPACTED_ITEMS = "ResetImpactedItems";
const UPDATE_IMPACTED_ITEMS = "UpdateImpactedItems";

const AffectedItemsGroupView = declare([TableWidgetConstructor, _GroupByRefMixinConstructor], {
	groupByRefAttr: ATTR_PARENT,
	groupByRefOptional: true,

	/** Actions: actions for DSSection groups */
	_sectionActions: null,

	/** @property {reflist} selectedAffectedItems - selected AffectedItems. Default empty = All will be displayed */
	selectedAffectedItems: null,

	/***
	 * @override
	 * C8FB-1994: When the view is grouped by AffectedItem Section then the group header row has table-row actions from AffectItem view
	 */
	onGroupByRefQueryLevel(ql: QueryLevel): void {
		// NOTE: this code assumes the first step in the path is "Child:AffectedItems" and that there is a second step in the path to get the results of the view
		const { cacheView, relRoot, urlRoot } = this._tableWidget();
		const path = relRoot.getOnlyPath();
		const pathSteps = pathGetSteps(path);
		const id = pathGetStepOperand(pathSteps[1]);
		const filterQuery = new Query().Attr(id, xml.OP_NOT_NULL);

		ql.actions = this._sectionActions;
		ql.groupDefaultsStore = new StoreNodesConstructor({
			cachingStore: cacheView,
			filterQuery,
			requestParameters: {
				[QRY_LIMIT_PATH]: getUrlPath(urlRoot, pathSteps[0]),
			},
			sortQuery: new Query().SortByAttr(xml.ATTR_NAME),
			typeNode: "AffectedItem",
		});
	},

	/**
	 * @override
	 * Inline Actions for each AffectedItem Section
	 */
	_resolveView() {
		const { defView } = this._tableWidget();
		const { m_id } = defView;

		const affectedItemCalculateImpItemPctReq = lang.mixin(new Request(), {
			m_module: "FormulatedProduct",
			m_operation: "AffectedItemCalculateImpactedItemPct",
		});

		const FetchAdditionalImpactedItemsReq = lang.mixin(new Request(), {
			m_module: "EngineeringChange",
			m_operation: "FetchAdditionalImpactedItems",
		});

		const addToAffectedItemsReq = lang.mixin(new Request(), {
			m_module: "EngineeringChange",
			m_operation: "AddToAffectedItems",
		});

		const resetImpactedItemsReq = lang.mixin(new Request(), {
			m_module: "EngineeringChange",
			m_operation: "ResetImpactedItems",
		});

		const updateImpactedItemsReq = lang.mixin(new Request(), {
			m_module: "EngineeringChange",
			m_operation: "FindImpactedItems",
		});

		if (defView.shouldAllowActions()) {
			const newActions = new Actions();
			const postAction = m_id === "MultiLevelImpactedItemsView" ? "refreshDataPreserveHierarchy" : "postActionRefreshData";
			const classControllerParams = { bShowWait: true };

			newActions.m_actions =
				[
					defView._addViewAction(UPDATE_IMPACTED_ITEMS, {
						classController: AffectedItemsGroupController,
						classControllerParams,
						m_accessAction: "_accessActionAffectedItemSection",
						m_preAction: "EngineeringChange.preActionAffectedItemUpdate",
						m_postAction: postAction,
						m_request: updateImpactedItemsReq,
					}),
					defView._addViewAction(AFFECTED_ITEM_CALCULATE_IMPACTED_ITEM_PCT, {
						classController: AffectedItemsGroupController,
						classControllerParams,
						m_accessAction: "_accessActionAffectedItemCalculate",
						m_postAction: m_id === "MultiLevelImpactedItemsView" ? "refreshDataPreserveHierarchy" : "postActionRedrawData",
						m_request: affectedItemCalculateImpItemPctReq,
					}),
					defView._addViewAction(FETCH_ADDITIONAL_IMPACTED_ITEMS, {
						classController: FetchAdditionalImpactedItemsGroupController,
						classControllerParams,
						m_accessAction: "_accessActionFetchAdditionalImpactedItems",
						m_preAction: "EngineeringChange.preActionAffectedItemUpdate",
						m_postAction: postAction,
						m_request: FetchAdditionalImpactedItemsReq,
					}),
					defView._addViewAction(ADD_TO_AFFECTED_ITEMS, {
						classController: AddToAffectedItemsGroupController,
						classControllerParams,
						m_accessAction: "_accessActionAffectedItemSection",
						m_postAction: postAction,
						dependencyPaths: ["Child:__Parent__/Child:AffectedItems"],
						m_request: addToAffectedItemsReq,
						m_viewRef: lang.mixin(new ViewRef(), {
							m_id: "ImpactedProducts",
						}),
					}),
					defView._addViewAction(RESET_IMPACTED_ITEMS, {
						classController: AffectedItemsGroupController,
						classControllerParams,
						m_accessAction: "_accessActionAffectedItemSection",
						m_postAction: postAction,
						m_request: resetImpactedItemsReq,
					}),
					defView._addViewAction(new Delete(), {
						classController: AffectedItemsGroupDeleteController,
						confirmAction: MessageDialog.BUTTON_DELETE,
						m_postAction:postAction,
					}),
				];
			this._sectionActions = newActions;
		}

		this.inherited(arguments);
	},

	_accessActionAffectedItemSection(ctx: Context, action: Action): boolean {
		// Show only if AffectedItem.ImpactedItems is not empty
		const sectionAffectedItem = this._getSectionAffectedItem(ctx);
		if (!sectionAffectedItem || sectionAffectedItem.ImpactedItems.length === 0) {
			return false;
		}
		return xml.nodeHasAccess(sectionAffectedItem, xml.RIGHT_WRITE);
	},

	_accessActionAffectedItemCalculate(ctx: Context, action: Action) {
		// Show only if AffectedItem.ItemType is Food/FormulatedMaterial/FoodIngredient
		const sectionAffectedItem = this._getSectionAffectedItem(ctx);
		if (!EC_ITEM_TYPE_FORMULATED_PRODUCT_TYPES.includes(sectionAffectedItem?.ItemType)) {
			return false;
		}
		return this._accessActionAffectedItemSection(ctx, action);
	},

	_accessActionFetchAdditionalImpactedItems(ctx: Context, action: Action) {
		return this._accessActionAffectedItemSection(ctx, action);
	},

	_getSectionAffectedItem(ctx: Context): C8Node {
		return ctx.cache.get(ctx.queryResult?.groupValue);
	},

	/**
	 * @override
	 * Add filter to get the results for selected AffectedItems.
	 */
	onCreateMainQueryLevel(ql: QueryLevel): void {
		const { selectedAffectedItems } = this;

		if (!selectedAffectedItems?.length) {
			return;
		}

		ql.onCreateStore = (store, bConfigurable, ql): void => {
			// bConfigurable == false => store is being used for the table view, true when finding filter values when configuring the custom view.
			// In the custom view dialog we still query for all values since the custom view applies to all items, not just what is in the switchboard.
			if (!bConfigurable) {
				const q: Query = new Query();
				const qOr: Query = q.Or();
				selectedAffectedItems.forEach((affectedItem) => {
					qOr.Node(xml.PROP_URL, xml.OP_EQUALS, affectedItem, "Child:__Parent__");
				});

				if (!q.isEmpty()) {
					store.filterQuery = q;
				}
			}
		};
	},

	/**
	 * @override
	 * Refresh table and switchboard whenever tab switch occurs
	 */
	onTabSwitch(): void {
		this._tableWidget().refreshOnTabSwitch = true;
		this.inherited(arguments);
	},

	/**
	 * @override
	 * Refresh Switchboard store after table refresh
	 */
	_refreshWidget(): void {
		const affectedItemSwitchboard: MultiSelectSwitchboard = this._tableWidget().getPlugin("SelectAffectedItems") as any as MultiSelectSwitchboard;
		affectedItemSwitchboard?.refreshStore(null, null);

		this.inherited(arguments);
	},

	/**
	 * Type conversion to widget
	 * NOTE: not sure why this is necessary, but can't find way around it
	 */
	_tableWidget(): TableWidget {
		return this as unknown as TableWidget;
	},
});

export = AffectedItemsGroupView;